// ===========================================
// RepeatableGroupField.jsx
// Campo genérico: lista editable de objetos, N filas, columnas 100%
// configurables desde JSONB (BD manda) -- select/text/number por
// columna, cada una con su propia dataSource si aplica. Primer caso de
// uso: satcausantes.ctabancaria (banco/cuenta/moneda) -- pero NO es
// especifico de bancos. Reutilizable para cualquier "lista corta
// editable dentro de un formulario" (ej. telefonos multiples de un
// cliente, contactos de notificacion, direcciones alternas).
// 100% inline -- sin modal, sin portal, dentro del flujo normal del
// formulario (mismo criterio ya aplicado con AutocompleteHeaderField).
//
// Estilos: usa EXACTAMENTE las mismas fuentes (inputStyles/selectStyles/
// labelStyles/errorStyles/botones_ui) que ya usa el resto de campos en
// SqlexecBuilder.jsx -- mismos nombres de propiedad, mismos fallbacks,
// mismo comportamiento de foco/boxShadow. No es un sistema de estilos
// propio.
//
// Navegación con Enter: useFormNavigation (hook compartido) está
// construido sobre schema.fields, una lista PLANA y ESTATICA -- no
// tiene forma de representar "fila N, columna X" dentro de un campo
// dinámico. Reutilizarlo tal cual hubiera requerido tocar su lógica
// central (getNavigableFields/getNextFieldIndex) para un caso que no
// contempla, con riesgo para el resto del formulario. En vez de eso,
// este componente implementa un Enter->siguiente-campo AUTOCONTENIDO,
// recorriendo los elementos enfocables dentro de su propio contenedor
// (mismo resultado para el usuario, cero cambios al hook compartido).
// ===========================================
import React, { useState, useEffect, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { secureFetch } from '../../utils/authInterceptor';

const RepeatableGroupField = ({
  field, value = [], onChange, disabled = false, error = null, touched = false,
  labelStyles = {}, inputStyles = {}, selectStyles = {}, errorStyles = {}, btnBase = {}, btnSecondary = {}
}) => {
  const {
    label, columns = [], uniqueBy = [],
    addLabel = 'Agregar',
    emptyLabel = 'Sin registros'
  } = field;
  const [columnOptions, setColumnOptions] = useState({});
  const [focusedCell, setFocusedCell] = useState(null); // `${index}-${col.name}`
  const containerRef = useRef(null);

  // Carga UNA vez las opciones de cada columna tipo select con dataSource --
  // mismo patron ya usado para selects de header/detalle en todo el sistema.
  useEffect(() => {
    columns.filter(c => c.type === 'select' && c.dataSource?.query).forEach(async (col) => {
      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        const r = await secureFetch('/api/sqlexec/query', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: col.dataSource.query })
        });
        const j = await r.json();
        if (j?.success) setColumnOptions(prev => ({ ...prev, [col.name]: j.data || [] }));
      } catch (e) {
        console.error(`Error cargando opciones de ${col.name}:`, e);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filaVacia = () => Object.fromEntries(
    columns.map(c => [c.name, c.defaultValue ?? (c.type === 'number' ? 0 : '')])
  );
  const agregarFila = () => onChange([...value, filaVacia()]);
  const quitarFila = (index) => onChange(value.filter((_, i) => i !== index));
  const actualizarFila = (index, colName, val) => {
    const nuevo = [...value];
    nuevo[index] = { ...nuevo[index], [colName]: val };
    onChange(nuevo);
  };

  // Enter -> siguiente control enfocable dentro del propio grupo (autocontenido,
  // ver nota arriba sobre por que no se reutiliza useFormNavigation aqui).
  const handleEnter = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const focusables = Array.from(
      containerRef.current?.querySelectorAll('select:not(:disabled), input:not(:disabled)') || []
    );
    const idx = focusables.indexOf(e.target);
    if (idx > -1 && idx < focusables.length - 1) {
      focusables[idx + 1].focus();
    } else if (idx === focusables.length - 1) {
      e.target.blur();
    }
  };

  // Duplicados segun uniqueBy -- array de nombres de columna, puede ser
  // compuesto (ej. ["idsatbancos","cuenta"] si algun caso futuro lo pide).
  const claveFila = (fila) => uniqueBy.map(k => (fila[k] ?? '').toString().trim()).join('||');
  const claveVacia = uniqueBy.map(() => '').join('||');
  const claves = value.map(claveFila);
  const esDuplicada = (index) => uniqueBy.length > 0 && claves[index] !== claveVacia
    && claves.filter(c => c === claves[index]).length > 1;

  // Completitud -- columnas con required:true deben estar llenas en cada fila.
  const celdaIncompleta = (fila, col) => col.required && (fila[col.name] === '' || fila[col.name] === null || fila[col.name] === undefined);
  const filaIncompleta = (fila) => columns.some(col => celdaIncompleta(fila, col));

  const showError = touched && error;
  // Tema 2: no se puede abrir una fila nueva mientras haya una fila
  // incompleta en proceso -- fuerza a terminarla primero.
  const hayFilaIncompleta = value.some(f => filaIncompleta(f));
  const agregarDeshabilitado = disabled || hayFilaIncompleta;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: labelStyles.marginBottom || '0.5rem' }}>
        <label className="block" style={{ fontSize: labelStyles.fontSize || '0.875rem', fontWeight: labelStyles.fontWeight || '500', color: labelStyles.textColor || '#374151' }}>
          {label}
        </label>
        {!disabled && (
          <button
            type="button" onClick={agregarFila} disabled={agregarDeshabilitado}
            title={hayFilaIncompleta ? 'Completa la fila actual antes de agregar otra' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.25rem',
              height: btnBase.height || '28px',
              paddingLeft: btnBase.padding?.x || '0.5rem', paddingRight: btnBase.padding?.x || '0.5rem',
              borderRadius: btnBase.borderRadius || '6px',
              borderWidth: btnBase.borderWidth || '1px', borderStyle: 'solid',
              borderColor: agregarDeshabilitado ? (btnBase.disabledBorderColor || '#e5e7eb') : (btnSecondary.borderColor || '#d1d5db'),
              backgroundColor: agregarDeshabilitado ? (btnBase.disabledBackgroundColor || '#f3f4f6') : (btnSecondary.backgroundColor || '#ffffff'),
              color: agregarDeshabilitado ? (btnBase.disabledTextColor || '#9ca3af') : (btnSecondary.textColor || '#374151'),
              fontSize: btnBase.fontSize || '0.75rem',
              cursor: agregarDeshabilitado ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={e => { if (!agregarDeshabilitado) e.currentTarget.style.backgroundColor = btnSecondary.hoverBackgroundColor || '#f9fafb'; }}
            onMouseLeave={e => { if (!agregarDeshabilitado) e.currentTarget.style.backgroundColor = btnSecondary.backgroundColor || '#ffffff'; }}
          >
            <Plus size={14} /> {addLabel}
          </button>
        )}
      </div>

      <div ref={containerRef} style={{ border: `1px solid ${inputStyles.borderColor || '#d1d5db'}`, borderRadius: inputStyles.borderRadius || '6px', overflow: 'hidden' }}>
        {value.length === 0 ? (
          <div style={{ padding: '0.5rem 0.75rem', fontSize: inputStyles.fontSize || '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
            {emptyLabel}
          </div>
        ) : value.map((fila, index) => {
          const duplicada = esDuplicada(index);
          const incompleta = filaIncompleta(fila);
          return (
            <div key={index} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.75rem',
              borderBottom: index < value.length - 1 ? '1px solid #f3f4f6' : 'none',
              backgroundColor: (duplicada || incompleta) ? '#fef2f2' : '#ffffff'
            }}>
              {columns.map(col => {
                const cellKey = `${index}-${col.name}`;
                const celdaMal = duplicada || celdaIncompleta(fila, col);
                const enFoco = focusedCell === cellKey;
                const bordeColor = celdaMal ? '#ef4444' : enFoco ? ((col.type === 'select' ? selectStyles : inputStyles).focusBorderColor || '#3b82f6') : ((col.type === 'select' ? selectStyles : inputStyles).borderColor || '#d1d5db');
                const boxShadowFoco = enFoco ? `0 0 0 ${(col.type === 'select' ? selectStyles : inputStyles).focusRingWidth || '2px'} ${(col.type === 'select' ? selectStyles : inputStyles).focusBorderColor || '#3b82f6'}40` : 'none';
                return (
                  <div key={col.name} style={{ flex: 1 }}>
                    {col.type === 'select' ? (
                      <select
                        value={fila[col.name] ?? ''} disabled={disabled}
                        onChange={e => actualizarFila(index, col.name, e.target.value)}
                        onFocus={() => setFocusedCell(cellKey)}
                        onBlur={() => setFocusedCell(null)}
                        onKeyDown={handleEnter}
                        style={{
                          width: '100%', height: selectStyles.height || '32px', fontSize: selectStyles.fontSize || '0.75rem',
                          fontFamily: selectStyles.fontFamily || 'inherit',
                          borderRadius: selectStyles.borderRadius || '6px', borderWidth: selectStyles.borderWidth || '1px', borderStyle: 'solid',
                          borderColor: bordeColor, outline: 'none', boxShadow: boxShadowFoco,
                          padding: `0 ${selectStyles.padding?.x || '0.5rem'}`,
                          backgroundColor: disabled ? '#f3f4f6' : (selectStyles.backgroundColor || '#ffffff'),
                          color: (fila[col.name] === '' || fila[col.name] == null) ? (selectStyles.placeholderColor || '#9ca3af') : (selectStyles.textColor || '#111827'),
                          opacity: disabled ? (selectStyles.disabledOpacity || '0.5') : '1'
                        }}
                      >
                        <option value="">{col.label}...</option>
                        {(columnOptions[col.name] || []).map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={col.type === 'number' ? 'number' : 'text'}
                        value={fila[col.name] ?? ''} placeholder={col.label} disabled={disabled}
                        onChange={e => actualizarFila(index, col.name, e.target.value)}
                        onFocus={() => setFocusedCell(cellKey)}
                        onBlur={() => setFocusedCell(null)}
                        onKeyDown={handleEnter}
                        style={{
                          width: '100%', height: inputStyles.height || '32px', fontSize: inputStyles.fontSize || '0.75rem',
                          fontFamily: inputStyles.fontFamily || 'inherit',
                          borderRadius: inputStyles.borderRadius || '6px', borderWidth: inputStyles.borderWidth || '1px', borderStyle: 'solid',
                          borderColor: bordeColor, outline: 'none', boxShadow: boxShadowFoco,
                          padding: `0 ${inputStyles.padding?.x || '0.75rem'}`,
                          backgroundColor: disabled ? '#f3f4f6' : (inputStyles.backgroundColor || '#ffffff'),
                          color: inputStyles.textColor || '#111827',
                          opacity: disabled ? (inputStyles.disabledOpacity || '0.5') : '1'
                        }}
                      />
                    )}
                  </div>
                );
              })}
              {!disabled && (
                <button
                  type="button" onClick={() => quitarFila(index)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px',
                    borderRadius: '4px', border: 'none', backgroundColor: 'transparent', color: '#9ca3af', cursor: 'pointer', flexShrink: 0
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {showError && <p style={{ fontSize: errorStyles.fontSize || '0.75rem', color: errorStyles.textColor || '#dc2626', marginTop: errorStyles.marginTop || '0.25rem' }}>{error}</p>}
    </div>
  );
};

export default RepeatableGroupField;
